import React, { useState } from "react";
import { FaUserShield, FaLock, FaEye, FaEyeSlash, FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import LoginAnimation from "../components/LoginAnimation";
import { useTheme } from "../context/ThemeContext";
import ThemeCustomizer from "../components/ui/ThemeCustomizer";
import Logo from "../Assets/HD Quality Logo.svg";
import LogoDark from "../Assets/HD Quality Logo 2.svg";
import AdminLoginImage from "../Assets/Admin_Image.png";

const ADMIN_FEATURES = [
  "Client & member management",
  "Tax filing, bookkeeping & USA formation",
  "Documents, payments & compliance tools",
];

const AdminLoginPage = () => {
  const [adminID, setAdminID] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showAnimation, setShowAnimation] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminID, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Admin login failed.");
      }

      const data = await response.json();
      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminID", data.adminID);
      if (data.name) localStorage.setItem("adminName", data.name);
      setMessage("Admin login successful! Redirecting...");
      setShowAnimation(true);
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex overflow-hidden ${isDarkMode ? "dark bg-dark-950" : "bg-gray-50"}`}>
      {showAnimation && <LoginAnimation />}

      {/* Left — image + branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative shrink-0 overflow-hidden">
        <img
          src={AdminLoginImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          aria-hidden
        />

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/85 via-primary-800/70 to-primary-950/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />

        {/* Decorative shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-secondary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-56 h-56 bg-primary-400/15 rounded-full blur-3xl" />
          <div className="absolute top-28 right-16 w-16 h-16 bg-white/10 rounded-2xl rotate-12" />
          <div className="absolute bottom-40 right-24 w-10 h-10 bg-secondary-400/20 rounded-xl rotate-45" />
        </div>

        <div className="relative z-10 flex flex-col h-full w-full p-10 xl:p-14">
          {/* Logo — top left */}
          <img src={LogoDark} alt="BooksComply" className="h-10 xl:h-11 w-auto drop-shadow-md self-start" />

          {/* Main copy — vertically centered */}
          <div className="flex-1 flex flex-col justify-center max-w-lg">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Welcome to BooksComply Admin
            </h1>
            <p className="text-base xl:text-lg text-white/80 leading-relaxed mb-10">
              Your control center for accounting, tax filing, and business compliance operations.
            </p>

            <ul className="space-y-4">
              {ADMIN_FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <FaCheckCircle className="w-4 h-4 text-white" />
                  </span>
                  <span className="text-white/90 text-sm xl:text-base font-medium">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} BooksComply. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right — login */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex flex-col min-h-screen bg-white dark:bg-dark-950">
        <header className="flex items-center justify-between gap-4 px-6 sm:px-8 lg:px-10 py-5 shrink-0 border-b border-gray-100 dark:border-dark-800">
          <img
            src={isDarkMode ? LogoDark : Logo}
            alt="BooksComply"
            className="h-9 sm:h-10 w-auto lg:invisible"
          />
          <ThemeCustomizer variant="auth" />
        </header>

        <main className="flex-1 overflow-y-auto flex items-center justify-center px-6 sm:px-8 lg:px-10 py-8 sm:py-10">
          <div className="w-full max-w-md">
            <div className="mb-8 text-center lg:text-left">
              <div className="inline-flex lg:hidden w-14 h-14 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl items-center justify-center shadow-lg mb-5">
                <FaUserShield className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Admin <span className="text-gradient">Login</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                Welcome back! Please sign in to continue.
              </p>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-5">
              <div>
                <label htmlFor="adminID" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                  Admin ID
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FaUserShield className="w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  </div>
                  <input
                    id="adminID"
                    type="text"
                    className="input-modern pl-11 bg-white dark:bg-dark-800"
                    placeholder="Enter your admin ID"
                    value={adminID}
                    onChange={(e) => setAdminID(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FaLock className="w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="input-modern pl-11 pr-11 bg-white dark:bg-dark-800"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <a
                  href="/forgetpassword"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-secondary-500 font-medium transition-colors"
                >
                  Forgot Password?
                </a>
              </div>

              <button type="submit" disabled={loading} className="btn-premium w-full py-3.5">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner w-5 h-5 border-2" />
                    Logging in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            {message && (
              <div
                className={`mt-6 p-4 rounded-xl text-center text-sm font-medium ${
                  message.includes("successful")
                    ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                    : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                }`}
              >
                {message}
              </div>
            )}

            <p className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
              Authorized personnel only. All access is monitored and logged.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLoginPage;
